import React from "react";
import { Form } from "react-crud-form";
import { useNavigate } from "react-router-dom";
import { FormField, FormItem, FormMessage } from "ui/components/form";
import { Input } from "ui/components/input";
import { Textarea } from "ui/components/textarea";
import { FormLabel } from "components/form";
import { FormActions } from "components/form/actions";
import { FormFieldDescription } from "components/form/description";
import { FileUpload } from "components/file-upload/FileUpload";
import { PermissionControlledInput } from "permissions/components/input";
import { assetUrl } from "utils";
import { testimonialSchema } from "./schema";
import { useTestimonialForm } from "./useTestimonialForm";

const COLLECTION = "testimonials";

const TestimonialForm = ({ clientId, testimonialId }) => {
  const navigate = useNavigate();
  const { form, requiredFields, handleCreate, handleUpdate, handleRead } =
    useTestimonialForm({ clientId });

  return (
      <Form
        form={form}
        create={handleCreate}
        read={handleRead}
        update={handleUpdate}
        itemId={testimonialId || "+"}
        schema={testimonialSchema}
      >
        {({ isCreating, createItem, isNewItem, isUpdating, updateItem, isDirty }) => {
          const rule = isNewItem ? "create:*" : "update:*";
          return (
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="feedback"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={rule}
                      collection={COLLECTION}
                      type="form"
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Feedback
                      </FormLabel>
                      <Textarea rows={5} {...field} value={field.value ?? ""} />
                    </PermissionControlledInput>
                    <FormFieldDescription collection={COLLECTION} />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="review_url"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={rule}
                      collection={COLLECTION}
                      type="form"
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Review URL
                      </FormLabel>
                      <Input
                        type="url"
                        placeholder="Clutch or Upwork review link"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </PermissionControlledInput>
                    <FormFieldDescription collection={COLLECTION} />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="video"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={rule}
                      collection={COLLECTION}
                      type="form"
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Video
                      </FormLabel>
                      <FileUpload
                        value={field.value}
                        onChange={field.onChange}
                        accept="video/*"
                        maxSize={200 * 1024 * 1024}
                        fileType={form.watch("video_type")}
                        resolveFileUrl={assetUrl}
                      />
                    </PermissionControlledInput>
                    <FormFieldDescription collection={COLLECTION} />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormActions
                isNewItem={isNewItem}
                isCreating={isCreating}
                isUpdating={isUpdating}
                isDirty={isDirty}
                createItem={() => createItem().then(() => navigate(-1))}
                updateItem={() => updateItem().then(() => navigate(-1))}
                showCancel={false}
                collection={COLLECTION}
                rule={rule}
              />
            </div>
          );
        }}
      </Form>
  );
};

export default TestimonialForm;
